// src/pages/finance-portal/payroll-runs/PayrollRunDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Card, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert, CircularProgress, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalculateIcon from '@mui/icons-material/Calculate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BookIcon from '@mui/icons-material/Book';
import BlockIcon from '@mui/icons-material/Block';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#fef3c7', color: '#92400e' },
    CALCULATED: { bg: '#dbeafe', color: '#1e40af' },
    APPROVED: { bg: '#e0e7ff', color: '#3730a3' },
    COMPLETED: { bg: '#dcfce7', color: '#166534' },
    VOID: { bg: '#f3f4f6', color: '#6b7280' },
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function SummaryCard({ label, value, bgcolor }) {
    return (
        <Box sx={{ p: 2, borderRadius: '12px', bgcolor, flex: 1, minWidth: 140 }}>
            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 700, color: '#1e293b' }}>
                {formatCurrency(value)}
            </Typography>
        </Box>
    );
}

export default function PayrollRunDetail() {
    const navigate = useNavigate();
    const { runId } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const [run, setRun] = useState(null);
    const [stubs, setStubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Action states
    const [calculating, setCalculating] = useState(false);
    const [approving, setApproving] = useState(false);
    const [generatingJournal, setGeneratingJournal] = useState(false);
    const [voidDialog, setVoidDialog] = useState(false);
    const [voiding, setVoiding] = useState(false);

    const fetchRunDetail = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/payroll/runs/${runId}`);
            if (res.data.success) {
                setRun(res.data.data);
                setStubs(res.data.data?.pay_stubs || res.data.data?.stubs || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load payroll run');
        } finally {
            setLoading(false);
        }
    }, [runId]);

    useEffect(() => { fetchRunDetail(); }, [fetchRunDetail]);

    const handleCalculate = async () => {
        setCalculating(true);
        try {
            await axiosInstance.post(`/api/finance/payroll/runs/${runId}/calculate`);
            enqueueSnackbar('Payroll calculated successfully.', { variant: 'success' });
            fetchRunDetail();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to calculate payroll.', { variant: 'error' });
        } finally {
            setCalculating(false);
        }
    };

    const handleApprove = async () => {
        setApproving(true);
        try {
            await axiosInstance.post(`/api/finance/payroll/runs/${runId}/approve`);
            enqueueSnackbar('Payroll approved.', { variant: 'success' });
            fetchRunDetail();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to approve payroll.', { variant: 'error' });
        } finally {
            setApproving(false);
        }
    };

    const handleGenerateJournal = async () => {
        setGeneratingJournal(true);
        try {
            await axiosInstance.post(`/api/finance/payroll/runs/${runId}/journal`);
            enqueueSnackbar('Journal entry generated successfully.', { variant: 'success' });
            fetchRunDetail();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to generate journal entry.', { variant: 'error' });
        } finally {
            setGeneratingJournal(false);
        }
    };

    const handleVoid = async () => {
        setVoiding(true);
        try {
            await axiosInstance.post(`/api/finance/payroll/runs/${runId}/void`);
            enqueueSnackbar('Payroll run voided.', { variant: 'success' });
            setVoidDialog(false);
            fetchRunDetail();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to void payroll run.', { variant: 'error' });
        } finally {
            setVoiding(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily }}>{error}</Alert>
                <Button onClick={() => navigate('/finance-portal/payroll-runs')} sx={{ fontFamily, mt: 2, textTransform: 'none' }}>
                    Back to Payroll Runs
                </Button>
            </Box>
        );
    }

    if (!run) return null;

    const statusStyle = STATUS_COLORS[run.status] || {};

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <IconButton onClick={() => navigate('/finance-portal/payroll-runs')} sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AccountBalanceIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                    {run.run_number || 'Payroll Run'}
                                </Typography>
                                <Chip
                                    label={getStatusLabel(run.status)}
                                    size="small"
                                    sx={{
                                        fontFamily, fontSize: '0.75rem', fontWeight: 700, height: 26,
                                        bgcolor: 'rgba(255,255,255,0.25)', color: '#fff',
                                    }}
                                />
                            </Box>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                Period: {formatDate(run.period_start)} - {formatDate(run.period_end)} | Pay Date: {formatDate(run.pay_date)}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', ml: 7 }}>
                        {run.status === 'DRAFT' && (
                            <Button
                                variant="contained"
                                startIcon={calculating ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon />}
                                onClick={handleCalculate}
                                disabled={calculating}
                                sx={{
                                    fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                    bgcolor: 'white', color: '#3b82f6',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Calculate
                            </Button>
                        )}
                        {run.status === 'CALCULATED' && (
                            <Button
                                variant="contained"
                                startIcon={approving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                                onClick={handleApprove}
                                disabled={approving}
                                sx={{
                                    fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                    bgcolor: 'white', color: ACCENT,
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Approve
                            </Button>
                        )}
                        {(run.status === 'APPROVED' || run.status === 'COMPLETED') && (
                            <Button
                                variant="contained"
                                startIcon={generatingJournal ? <CircularProgress size={16} color="inherit" /> : <BookIcon />}
                                onClick={handleGenerateJournal}
                                disabled={generatingJournal}
                                sx={{
                                    fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                    bgcolor: 'white', color: '#8b5cf6',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Generate Journal Entry
                            </Button>
                        )}
                        {run.status !== 'VOID' && run.status !== 'COMPLETED' && (
                            <Button
                                variant="outlined"
                                startIcon={<BlockIcon />}
                                onClick={() => setVoidDialog(true)}
                                sx={{
                                    fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                    borderColor: 'rgba(255,255,255,0.5)', color: 'rgba(255,255,255,0.9)',
                                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                                }}
                            >
                                Void
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Summary Cards */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <SummaryCard label="Total Gross" value={run.total_gross} bgcolor="#f0fdf4" />
                    <SummaryCard label="Total Taxes" value={run.total_taxes} bgcolor="#fef9c3" />
                    <SummaryCard label="Total Deductions" value={run.total_deductions} bgcolor="#fce7f3" />
                    <SummaryCard label="Total Net Pay" value={run.total_net} bgcolor="#dbeafe" />
                    <SummaryCard label="Employer Taxes" value={run.total_employer_taxes} bgcolor="#e0e7ff" />
                    <SummaryCard label="Total Cost" value={run.total_cost} bgcolor="#fecaca" />
                </Box>

                {/* Pay Stubs Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>
                            Pay Stubs ({stubs.length})
                        </Typography>
                    </Box>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Employee', 'Gross Pay', 'Federal Tax', 'Social Security', 'Medicare', 'State Tax', 'Deductions', 'Net Pay'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stubs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <AttachMoneyIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>
                                                {run.status === 'DRAFT' ? 'Calculate payroll to generate pay stubs' : 'No pay stubs found'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : stubs.map(stub => {
                                    const empName = stub.employee_name || [stub.first_name, stub.last_name].filter(Boolean).join(' ') || '—';
                                    return (
                                        <TableRow
                                            key={stub.id}
                                            hover
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                                            onClick={() => navigate(`/finance-portal/pay-stubs/${stub.id}`)}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {empName}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(stub.gross_pay)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatCurrency(stub.federal_tax)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatCurrency(stub.social_security_tax || stub.ss_tax)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatCurrency(stub.medicare_tax)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatCurrency(stub.state_tax)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatCurrency(stub.total_deductions || stub.deductions)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: ACCENT }}>
                                                {formatCurrency(stub.net_pay)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Void Confirmation Dialog */}
            <Dialog open={voidDialog} onClose={() => setVoidDialog(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Void Payroll Run {run.run_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will void the payroll run and all associated pay stubs. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setVoidDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleVoid}
                        disabled={voiding}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                    >
                        {voiding ? 'Voiding...' : 'Void Run'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
